import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { v4 as uuidv4 } from 'uuid'
import MultiStreamsMixer from 'multistreamsmixer'
import { timeNow } from '../../util/date'

/**
 * @typedef {{
*      callId: string,
*      callFrom: string,
*      callTo: string,
*      callStatus: string,
*      callFromName: string,
*      callFromPhone: string,
*      callFromPhotoUser: string,
*      code: string,
*      type: string,
*      duration: number,
*      start_at: string,
*      end_at: string,
*      end_type: string,
*      recordingFile: File,
*      sourceId: string,
*      destinationId: string,
*      sign: string
* }} CallType
*/

/**
* @typedef {{
*      clientId: string, 
*      sign: string, 
*      name: string,
*      phone: string,
*      image: string
* }} User
*/

export const GroChatVoipContext = React.createContext({
    /**
     * @description Calls user by Client Id. It will ask for microphone permission
     * @type {(clientId: string) => void}
     */
    call: null,

    /**
     * @description Answers call
     * @type {() => void}
     */
    answer: null,

    /**
     * @description Hang up call
     * @type {() => void}
     */
    hangUp: null,

    /**
     * @type {React.RefObject<HTMLAudioElement>}
     */
    remoteAudioRef: undefined,

    /**
     * @type {boolean}
     */
    isCalling: false, 

    /**
     * @type {boolean}
     */
    isRinging: false, 
    
    /**
     * @type {boolean}
     */
    isOnCall: false,

    /**
     * @type {boolean}
     */
    isCallEnded: false,

    /**
     * @type {boolean}
     */
    isMuted: false, 

    /**
     * @type {number}
     */
    duration: 0,

    /**
     * @type {() => CallType|null}
     */
    getCall: null,

    /**
     * @type {(callback: (currentCall: CallType) => void) => void}
     */
    onCall: null,

    /**
     * @type {(callback: (endCall: () => void, currentCall: CallType) => void) => void}
     */
    onCallEnded: null,

    /**
     * @type {() => void}
     */
    toggleVoice: null,

    /**
     * @type {(fileName: string) => void}
     */
    setRecordingFileName: null,

    /**
     * @type {(key: string, value: any) => void}
     */
    setContext: null,

    /**
     * @type {(key: string) => any}
     */
    getContext: null,
})

/**
 * @extends {React.Component<GroChatVoipProvider.propTypes>}
 */
class GroChatVoipProvider extends Component {
    constructor(props) {
        super(props)

        /**
         * @type {React.RefObject<HTMLAudioElement>}
         */
        this.ringingAudioRef = React.createRef()

        /**
         * @type {React.RefObject<HTMLAudioElement>}
         */
        this.hangUpAudioRef = React.createRef()

        /**
         * @type {React.RefObject<HTMLAudioElement>}
         */
        this.incomingCallAudioRef = React.createRef()

        /**
         * @type {React.RefObject<HTMLAudioElement>}
         */
        this.remoteAudioRef = React.createRef()
        
        /**
         * @type {NodeJS.Timeout|null}
         */
        this.hangUpTimeout = null

        this.hideAudioStyle = {
            width: 0,
            height: 0,
            visibility: 'hidden'
        }

        this.state = {
            /**
             * @type {RTCPeerConnection|null}
             */
            localPeer: null,

            /**
             * @type {MediaStream|null}
             */
            stream: null,

            /**
             * @type {CallType|null}
             */
            currentCall: null,

            /**
             * @type {boolean}
             */
            isCalling: false, 

            /**
             * @type {boolean}
             */
            isRinging: false,

            /**
             * @type {boolean}
             */
            isOnCall: false,

            /**
             * @type {boolean}
             */
            isCallEnded: false,

            /**
             * @type {boolean}
             */
            isMuted: false, 

            /**
             * @type {number}
             */
            duration: 0,

            /**
             * @type {MediaRecorder}
             */
            mediaRecorder: null,

            /**
             * @type {{ fileName: string, extension: string }}
             */
            recording: {
                fileName: 'audio.mp3'
            },

            /**
             * @type {Array<Promise<(endCall: () => void, currentCall: CallType) => void>>}
             */
            onCallEndedCallbacks: [],

            /**
             * @type {Array<Promise<(currentCall: CallType|null) => void>}
             */
            onCallCallbacks: [],

            /**
             * @type {any}
             */
            context: null,
        }
    }

    /**
     * @param {{ ws: WebSocket }} prevProps 
     * @param {{ isMuted: bool, isCalling: bool, isRinging: bool }} prevState
     */
    componentDidUpdate(prevProps, prevState) {
        if (prevProps.ws !== this.props.ws && this.props.ws) {
            this.initiateListener()
        }

        if (this.state.isMuted !== prevState.isMuted) {
            if (this.state.stream) {
                this.state.stream.getAudioTracks().forEach(track => {
                    track.enabled = !this.state.isMuted
                })
            }
        }
    }

    componentDidMount() {
        /**
         * Loop Ringing Sound
         */
        if (this.ringingAudioRef.current) {
            this.ringingAudioRef.current.volume = this.props.ringingVolume
            this.ringingAudioRef.current.addEventListener('ended', this.playRingingSound)
        }

        if (this.hangUpAudioRef.current) {
            this.hangUpAudioRef.current.volume = this.props.hangUpVolume
        }
    }

    componentWillUnmount() {
        this.ringingAudioRef.current.removeEventListener('ended', this.playRingingSound)
        this.props.ws.removeEventListener('message', this.handleMessage)
    }

    playRingingSound = () => {
        if (!this.ringingAudioRef.current) {
            return
        }

        this.ringingAudioRef.current.currentTime = 0
        setTimeout(() => {
            if (this.state.isRinging || this.state.isCalling) {
                this.ringingAudioRef.current.play()
            }
        }, this.props.ringingDelay * 1000)
    }

    playHangUpSound = () => {
        if (!this.hangUpAudioRef.current) {
            return
        }

        this.hangUpAudioRef.current.currentTime = 0
        this.hangUpAudioRef.current.play()
    }

    /**
     * @description Calls user by Client ID. It will ask for microphone permission
     * @param {string} clientId
     */
    call = async (clientId) => {
        let stream;

        try {
            stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        } catch(err) {
            throw err
        }

        const peer = new RTCPeerConnection(this.props.config)
        const callId = uuidv4()

        peer.ontrack = this.playRemoteStream
        peer.onicecandidate = (e) => this.sendIceCandidate(e, clientId, callId, this.props.user.clientId, clientId)

        stream.getAudioTracks().forEach(track => {
            track.enabled = !this.state.isMuted
            peer.addTrack(track, stream)
        })

        const offer = await peer.createOffer()
        peer.setLocalDescription(offer)

        this.setState({
            localPeer: peer,
            stream,
            isCalling: true
        })

        this.sendOffer(callId, offer, clientId)
        this.startHangUpTimeout(this.props.maxCallingDuration)
    }

    /**
     * @param {number} seconds 
     */
    startHangUpTimeout = (seconds = 0) => {
        this.hangUpTimeout = setTimeout(() => this.hangUp(), seconds * 1000)
    }

    /**
     * @param {string} callId
     * @param {RTCOfferOptions} offer
     * @param {string} clientId 
     */
    sendOffer = (callId, offer, clientId) => {
        const data = JSON.stringify({
            type: 'webrtc',
            message: {
                is_group: false,
                source_id: this.props.user.clientId,
                destination_id: clientId,
                identity: {
                    client_id: this.props.user.clientId,
                    sign: this.props.user.sign
                },
                message_detail: {
                    message_model: {
                        call_id: callId,
                        call_from: this.props.user.clientId,
                        call_to: clientId,
                        call_status: 'offer',
                        start_call: 0,
                        end_call: 0,
                        duration: 0,
                        is_reject: 0,
                        call_from_name: this.props.user.name,
                        call_from_phone: this.props.user.phone,
                        call_from_photo_user: this.props.user.image,
                        code: JSON.stringify(offer)
                    }
                }
            }
        })

        this.props.ws.send(data)
    }

    /**
     * @description Answers call 
     */
    answer = async () => {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        const currentCall = this.state.currentCall
        const peer = new RTCPeerConnection(this.props.config)

        peer.ontrack = this.playRemoteStream
        peer.onicecandidate = (e) => this.sendIceCandidate(e, currentCall.callFrom, currentCall.callId, currentCall.callFrom, currentCall.callTo)

        stream.getAudioTracks().forEach(track => peer.addTrack(track, stream))

        const answer = await peer.createAnswer()
        peer.setLocalDescription(answer)

        this.setState({
            localPeer: peer,
            stream,
        })

        this.sendAnswer(answer)
    }

    /**
     * @param {RTCAnswerOptions} answer
     */
    sendAnswer = (answer) => {
        const currentCall = this.state.currentCall
        const data = JSON.stringify({
            type: 'webrtc',
            message: {
                is_group: false,
                source_id: this.props.user.clientId,
                destination_id: currentCall.callFrom,
                identity: {
                    client_id: this.props.user.clientId,
                    sign: this.props.user.sign
                },
                message_detail: {
                    message_model: {
                        call_id: currentCall.callId,
                        call_from: currentCall.callFrom,
                        call_to: currentCall.callTo,
                        call_status: 'answer',
                        start_call: 0,
                        end_call: 0,
                        duration: 0,
                        is_reject: 0,
                        call_from_name: currentCall.callFromName,
                        call_from_phone: currentCall.callFromPhone,
                        call_from_photo_user: currentCall.callFromPhotoUser,
                        code: JSON.stringify(answer)
                    }
                }
            }
        })

        this.props.ws.send(data)
    }

    hangUp = async () => {
        const currentCall = this.state.currentCall
        if (!currentCall) {
            return
        }

        clearTimeout(this.hangUpTimeout)

        const data = {
            type: 'webrtc',
            message: {
                is_group: false,
                source_id: currentCall.sourceId,
                destination_id: currentCall.destinationId,
                identity: {
                    client_id: currentCall.sourceId,
                    sign: currentCall.sign
                },
                message_detail: {
                    message_model: {
                        call_id: currentCall.callId,
                        call_from: currentCall.callFrom,
                        call_to: currentCall.callTo,
                        call_status: 'end',
                        start_call: 0,
                        end_call: 0,
                        duration: 0,
                        is_reject: 0,
                        call_from_name: currentCall.callFromName,
                        call_from_phone: currentCall.callFromPhone,
                        call_from_photo_user: currentCall.callFromPhotoUser,
                    }
                }
            }
        }

        if (currentCall.type === 'offer' && !this.state.isOnCall) {
            data.message.message_detail.message_model.call_status = 'missed'
        }

        this.props.ws.send(JSON.stringify(data))
    }

    /**
     * @returns {CallType|null}
     */
    getCall = () => {
        return this.state.currentCall
    }

    /**
     * @param {string} key
     * @param {any} value
     */
    setContext = (key, value) => {
        let ctx = this.state.context

        if (!ctx) {
            ctx = {
                [key]: value
            }

            return this.setState({
                context: ctx
            })
        }

        const newCtx = {
            ...ctx,
            [key]: value
        }

        return this.setState({
            context: newCtx
        })
    }

    /**
     * @param {string} key
     * @returns {any}
     */
    getContext = (key) => {
        if (!this.state.context) {
            return null
        }

        return this.state.context[key]
    }

    /**
     * @param {string} fileName
     */
    setRecordingFileName = (fileName) => {
        if (!fileName) {
            fileName = 'audio.mp3'
        }

        return this.setState({
            recording: {
                ...this.state.recording,
                fileName
            }
        })
    }

    /**
     * @param {(currentCall: CallType|null) => void} callback
     */
    onCall = (callback) => {
        /**
         * @param {CallType|null} currentCall 
         */
        const asyncCallback = async (currentCall) => {
            callback(currentCall)
        }

        const callbacks = this.state.onCallCallbacks
        callbacks.push(asyncCallback)
        
        this.setState({
            onCallCallbacks: callbacks
        })
    }

    /**
     * @param {(recordedAudioFile: File) => void} callback
     */
    onCallEnded = (callback) => {
        /**
         * @param {() => void} endCall
         * @param {File} recordedAudioFile 
         * @param {number} duration 
         */
        const asyncCallback = async (endCall, recordedAudioFile, duration) => {
            callback(endCall, recordedAudioFile, duration)
        }

        const callbacks = this.state.onCallEndedCallbacks
        callbacks.push(asyncCallback)
        
        this.setState({
            onCallEndedCallbacks: callbacks
        })
    }

    /**
     * @param {CallType} currentCall 
     */
    callOnCallCallbacks = async (currentCall) => {
        Promise.all(this.state.onCallCallbacks.map((cb) => cb(currentCall)))
    }

    /**
     * @param {CallType} currentCall 
     */
    callOnCallEndedCallbacks = async (currentCall) => {
        Promise.all(this.state.onCallEndedCallbacks.map((cb) => cb(this.endCall, currentCall)))
    }

    endCall = async () => {
        if (!this.state.currentCall) {
            return
        }

        return this.setState({
            localPeer: null,
            stream: null,
            isCallEnded: false,
            currentCall: null,
            duration: 0,
            isMuted: false
        })
    }

    /**
     * @param {RTCTrackEvent} e
     */
    playRemoteStream = async (e) => {
        this.startCallDuration()

        this.stopRingingSound()
        await this.embedRemoteAudio(e.streams[0])
        
        this.recordAudio(this.state.stream, e.streams[0])

        this.setState({
            isOnCall: true,
            isRinging: false,
            isCalling: false
        })
    }

    toggleVoice = () => {
        this.setState({
            isMuted: !this.state.isMuted
        })
    }

    /**
     * 
     * @param {MediaStream} remoteStream 
     * @returns {Promise<void>}
     */
    embedRemoteAudio = (remoteStream) => {
        this.remoteAudioRef.current.srcObject = remoteStream
        return this.remoteAudioRef.current.play()
    }
    
    startCallDuration = () => {
        const start = Math.floor(new Date().getTime() / 1000)

        const interval = setInterval(() => {
            if (!this.state.isOnCall) {
                return clearInterval(interval)
            }

            const end = Math.floor(new Date().getTime() / 1000)
            this.setState({
                duration: end - start
            })
        }, 1000)
    }

    /**
     * @param {MediaStream} localStream 
     * @param {MediaStream} remoteStream 
     */
    recordAudio = (localStream, remoteStream) => {
        const streams = new MultiStreamsMixer([localStream, remoteStream])
        const mediaRecorder = new MediaRecorder(streams.getMixedStream())

        mediaRecorder.start()

        this.setState({ mediaRecorder })
    }

    /**
     * 
     * @returns {File|null}
     */
    getRecordedAudio = async () => {
        if (!this.state.mediaRecorder) {
            return null
        }

        const mediaRecorder = this.state.mediaRecorder
        if (!mediaRecorder) {
            return null
        }

        const recordedAudioFile = new Promise((resolve) => {
            mediaRecorder.ondataavailable = (e) => {
                const blob = new Blob([e.data], { type: 'audio/mpeg' })
                const fileName = this.state.recording.fileName
                const recordedAudioFile = new File([blob], fileName, { type: 'audio/mpeg' })

                resolve(recordedAudioFile)
            }

            mediaRecorder.stop()
        })

        return await recordedAudioFile
    }

    stopRingingSound = () => {
        if (this.ringingAudioRef.current) {
            this.ringingAudioRef.current.pause()
        }
    }

    stopStream = () => {
        this.state.stream?.getAudioTracks().forEach(track => track.stop())
    }

    /**
     * @param {RTCPeerConnectionIceEvent} e 
     * @param {string} destinationId
     * @param {string} callId 
     * @param {string} callFrom 
     * @param {string} callTo 
     */
    sendIceCandidate = (e, destinationId, callId, callFrom, callTo) => {
        if (!e.candidate) {
            return
        }
        
        const candidate = e.candidate
        const code = JSON.stringify({
            sdpMid: candidate.sdpMid,
            sdpMLineIndex: candidate.sdpMLineIndex,
            sdp: candidate.candidate
        })
        
        const data = JSON.stringify({
            type: "webrtc",
            message: {
                is_group: false,
                source_id: this.props.user.clientId,
                destination_id: destinationId,
                identity: {
                    client_id: this.props.user.clientId,
                    sign: this.props.user.sign
                },
                message_detail: {
                    message_model: {
                        call_id: callId,
                        call_from: callFrom,
                        call_to: callTo,
                        call_status: "candidate",
                        start_call: 0,
                        end_call: 0,
                        duration: 0,
                        is_reject: 0,
                        name: "",
                        phone: "",
                        call_from_photo_user: "",
                        code: code,
                    }
                }
            }
        })

        this.props.ws.send(data)
    }

    initiateListener = () => {
        const ws = this.props.ws
        
        ws.addEventListener('message', this.handleMessage)
        ws.addEventListener('close', this.handleClose)
    }

    handleClose = (e) => {
        if (e.code !== 1005) {
            return
        }

        this.stopStream()
        this.stopRingingSound()
        this.endCall()
        this.setState({
            isCalling: false, 
            isCallEnded: false,
            isOnCall: false,
            isRinging: false
        })
    }

    handleMessage = (e) => {
        const data = JSON.parse(e.data)

        if (data.type !== "webrtc") {
            return
        }

        this.handleWebRTCMessage(data)
    }

    /**
     * @param {object} data 
     */
    handleWebRTCMessage = (data) => {
        const messageModel = data.message.message_detail.message_model
        const callStatus = messageModel.call_status

        let sdp

        try {
            sdp = JSON.parse(messageModel.code)
        } catch {
            sdp = null
        }

        switch(callStatus) {
            /*
                Receive Call
            */
            case "offer" :
                this.handleOffer(messageModel)
                break
            /*
                Call Answered
            */
            case "answer" :
                this.handleAnswer(sdp)
                break
            /*
                Receive Ice Candidate
            */
            case "candidate" :
                this.handleCandidate(sdp)
                break
            /*
                Ringing
            */
            case "ringing" :
                this.handleRingingCall()
                break
            /*
                Call Rejected
            */
            case "missed" :
                this.handleMissedCall()
                break
            /*
                Call Rejected
            */
            case "reject" :
                this.handleCallRejected()
                break
            /*
                Call Ended
            */
            case "end" : 
                this.handleCallEnded()
                break
            default :
                return
        }
    }

    handleOffer = (messageModel) => {
        if (messageModel.call_from !== this.props.user.clientId) {
            return
        }

        const currentCall = {
            callId: messageModel.call_id,
            callFrom: messageModel.call_from,
            callTo: messageModel.call_to,
            callStatus: messageModel.call_status,
            callFromName: messageModel.call_from_name,
            callFromPhone: messageModel.call_from_phone,
            callFromPhotoUser: messageModel.call_from_photo_user,
            code: messageModel.code,
            type: 'offer',
            duration: 0,
            start_at: timeNow(),
            end_at: '',
            recordingFile: null,
            sourceId: messageModel.call_from,
            destinationId: messageModel.call_to,
            sign: this.props.user.sign
        }


        this.setState({ currentCall })
        this.callOnCallCallbacks(currentCall)

        this.ringingAudioRef.current.currentTime = 0
        this.ringingAudioRef.current.play()
    }

    handleAnswer = (sdp) => {
        if (!this.state.localPeer) {
            return
        }

        clearTimeout(this.hangUpTimeout)

        sdp.type = 'answer'
        this.state.localPeer.setRemoteDescription(new RTCSessionDescription(sdp))
    }

    handleCandidate = (sdp) => {
        if (!this.state.localPeer || !this.state.localPeer.remoteDescription) {
            return
        }

        const candidate = new RTCIceCandidate({
            sdpMid: sdp.sdpMid,
            sdpMLineIndex: sdp.sdpMLineIndex,
            candidate: sdp.sdp
        })

        this.state.localPeer.addIceCandidate(candidate)
    }

    handleRingingCall = async () => {
        if (this.state.isRinging || this.state.isOnCall) {
            return
        }

        this.setState({
            isCalling: false, 
            isRinging: true
        })
    }

    handleMissedCall = async () => {
        if (this.state.isCallEnded || !this.state.currentCall) {
            return
        }

        this.setState({
            isCalling: false, 
            isCallEnded: true,
            isOnCall: false,
            isRinging: false
        })

        clearTimeout(this.hangUpTimeout)

        this.stopRingingSound()
        this.stopStream()
        this.playHangUpSound()

        this.remoteAudioRef.current?.pause()
        this.remoteAudioRef.current.srcObject = null
        
        this.state.localPeer?.close()

        const currentCall = this.state.currentCall
        currentCall.duration = this.state.duration
        currentCall.end_at = timeNow()
        currentCall.end_type = "missed"
        
        this.callOnCallEndedCallbacks(currentCall)
    }

    handleCallRejected = () => {
        if (this.state.isCallEnded || !this.state.currentCall) {
            return
        }

        this.setState({
            isCalling: false, 
            isCallEnded: true,
            isOnCall: false,
            isRinging: false
        })

        clearTimeout(this.hangUpTimeout)

        this.stopRingingSound()
        this.stopStream()
        this.playHangUpSound()

        this.remoteAudioRef.current?.pause()
        this.remoteAudioRef.current.srcObject = null
        this.state.localPeer?.close()

        const currentCall = this.state.currentCall
        currentCall.duration = this.state.duration
        currentCall.end_at = timeNow()
        currentCall.end_type = "reject"
        
        this.callOnCallEndedCallbacks(currentCall)
    }

    handleCallEnded = async () => {
        if (this.state.isCallEnded || !this.state.currentCall) {
            return
        }

        this.setState({
            isCalling: false, 
            isCallEnded: true,
            isOnCall: false,
            isRinging: false
        })

        clearTimeout(this.hangUpTimeout)

        this.stopRingingSound()
        this.stopStream()
        this.playHangUpSound()
        
        this.remoteAudioRef.current?.pause()
        this.remoteAudioRef.current.srcObject = null
        this.state.localPeer?.close()
        
        const currentCall = this.state.currentCall
        currentCall.duration = this.state.duration
        currentCall.end_at = timeNow()
        currentCall.end_type = "end"
        currentCall.recordingFile = await this.getRecordedAudio()

        this.callOnCallEndedCallbacks(currentCall)
    }

    render() {
        const value = {
            call:                 this.call,
            answer:               this.answer,
            hangUp:               this.hangUp,
            remoteAudioRef:       this.remoteAudioRef,
            isCalling:            this.state.isCalling,
            isRinging:            this.state.isRinging,
            isOnCall:             this.state.isOnCall,
            isCallEnded:          this.state.isCallEnded,
            duration:             this.state.duration,
            getCall:              this.getCall,
            onCall:               this.onCall,
            onCallEnded:          this.onCallEnded,
            onCallEndedCallbacks: this.state.onCallEndedCallbacks,
            toggleVoice:          this.toggleVoice,
            isMuted:              this.state.isMuted,
            setRecordingFileName: this.setRecordingFileName,
            setContext:           this.setContext,
            getContext:           this.getContext
        }
        
        return (
            <GroChatVoipContext.Provider value={value}>
                <audio controls ref={this.remoteAudioRef} style={this.hideAudioStyle}></audio>
                <audio controls ref={this.ringingAudioRef} style={this.hideAudioStyle} src={this.props.ringingSoundSrc}></audio>
                <audio controls ref={this.hangUpAudioRef} style={this.hideAudioStyle} src={this.props.hangUpSoundSrc}></audio>
                <audio controls ref={this.incomingCallAudioRef} style={this.hideAudioStyle}></audio>
                { this.props.children }
            </GroChatVoipContext.Provider>
        )
    }
}

GroChatVoipProvider.defaultProps = {
    user: {
        clientId: '',
        sign: '',
        name: '',
        phone: '',
        image: ''
    },
    maxCallingDuration: 30,
    ringingDelay: 3,
    ringingVolume: 1,
    ringingSoundSrc: undefined,
    hangUpVolume: 1,
    hangUpSoundSrc: undefined
}

GroChatVoipProvider.propTypes = {
    /**
     * @type {WebSocket}
     */
    ws: PropTypes.object,

    /**
     * @type {RTCConfiguration}
     */
    config: PropTypes.object,

    /**
     * @type {User}
     */
    user: PropTypes.object,

    /**
     * @type {number}
     */
    maxCallingDuration: PropTypes.number,

    /**
     * @type {number}
     */
    ringingDelay: PropTypes.number,

    /**
     * @type {number}
     */
    ringingVolume: PropTypes.number,

    /**
     * @type {string|undefined}
     */
    ringingSoundSrc: PropTypes.string,

    /**
     * @type {number}
     */
    hangUpVolume: PropTypes.number,
    
    /**
     * @type {string|undefined}
     */
    hangUpSoundSrc: PropTypes.string
}

export default GroChatVoipProvider